<script setup lang="ts">
import { computed } from 'vue'

import { getClasses } from '~/js/composables/classes'
import { Analytics } from '~/js/interfaces/analytics'

import IconFa from '~/vue/_teammasters/atoms/icons/IconFa.vue'

interface Props {
    content?: string,
    disabled?: boolean
    ga?: Analytics | null,
    icon: string,
    theme?: 'light' | 'dark' | null,
    type?: 'a' | 'button' | 'div' | 'submit' | null,
    url?: string | null,
    utilities?: string
}

const props = withDefaults(defineProps<Props>(), {
    content: '',
    disabled: false,
    theme: null,
    type: 'button',
    url: null,
    utilities: '',
    utilitiesContent: '',
    utilitiesIcon: '',
})

const classes = {
    _default: {
        _default: 'inline-flex items-center justify-center cursor-pointer',
        display: 'rounded-full',
        size: 'w-10 h-10',
        padding: 'py-2 gap-2',
        text: 'text-xl font-semibold font-primary',
        hover: 'hover:underline decoration-2 underline-offset-6',
        focus: 'focus-visible:ring-2 focus-visible:ring-offset-white dark:ring-offset-black focus-visible:ring-offset-2 focus-visible:outline-none',
        transition: 'transition-colors duration-200 transform-gpu ease-in-out',
    },
    light: {
        color: 'bg-neutral-900 text-white',
        hover: 'hover:bg-white hover:text-neutral-900',
        focus: 'focus-visible:ring-indigo',
    },
    dark: {
        border: 'dark:border-none',
        color: 'dark:bg-white dark:text-neutral-900',
        hover: 'dark:hover:bg-neutral-900 dark:hover:text-white',
        focus: 'dark:focus-visible:ring-yellow',
    },
}

const getType = () => {
    return props.type == 'submit' ? 'button' : props.type
}

const attributes = computed(() => {
    if (props.type === 'div') {
        return {}; // No attributes for div
    }

    return {
        title: props.ga?.label ?? props.content,
        'data-tag-category': props.ga?.category ?? '',
        'data-tag-action': props.ga?.action ?? '',
        'data-tag-label': props.ga?.label ?? '',
    }
})

</script>

<template>
    <component
        :is="getType()"
        :class="[
            getClasses(classes._default), 
            getClasses(classes.light), 
            getClasses(classes.dark), 
            utilities
        ]"
        :href="url"
        :type="type"
        :data-theme="theme"
        v-bind="attributes"
    >
        <IconFa :icon="icon"/>
    </component>
</template>